import { IMAGE_LIMITS, POLYGLOT_SCAN_SIZE } from '../constants';
import { getSize, getType, readBytes } from '../fileInputUtils';
import type { FileInput, ValidationResult } from '../types';
import { formatBytes } from '../utils';

/**
 * Validates image files by checking magic bytes match claimed format.
 * Supports both File objects and streaming from ZIP entries.
 */
export async function validateImage(input: FileInput): Promise<ValidationResult> {
  const fileType = getType(input);
  if (!fileType.startsWith('image/')) {
    return { valid: false, reason: 'Invalid file type' };
  }

  const size = getSize(input);
  if (size < IMAGE_LIMITS.min) {
    return { valid: false, reason: `Too small — minimum valid image is ${IMAGE_LIMITS.min} bytes` };
  }
  if (size > IMAGE_LIMITS.max) {
    return {
      valid: false,
      reason: `Exceeds max size (${formatBytes(IMAGE_LIMITS.max)})`,
    };
  }

  // Read first chunk for magic byte + polyglot detection (memory-safe)
  const scanSize = Math.min(size, POLYGLOT_SCAN_SIZE);
  const bytes = await readBytes(input, 0, scanSize);

  // Validate magic bytes - accept any valid image format regardless of extension
  const isPNG = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
  const isJPEG = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  const isGIF = bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46;
  const isWebP =
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50;

  // Accept file if it has ANY valid image magic bytes (allows extension mismatches)
  if (!isPNG && !isJPEG && !isGIF && !isWebP) {
    return { valid: false, reason: 'not a valid image format' };
  }

  // Polyglot detection: only for very small files with validated structure
  // Compressed data has random byte patterns - need to verify actual headers
  const POLYGLOT_SIZE_THRESHOLD = 2 * 1024; // 2KB - real polyglots are tiny crafted files

  if (size <= POLYGLOT_SIZE_THRESHOLD) {
    // Check for embedded SVG/HTML/XML with scripts (XSS vectors)
    // Require multiple indicators to avoid false positives in compressed icon data
    const text = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    const hasXmlDecl = text.includes('<?xml');
    const hasSvgTag = text.includes('<svg');
    const hasHtmlTag = text.includes('<html');
    const hasScriptTag = text.includes('<script');
    const hasEventHandler = text.includes('onload=') || text.includes('onerror=');

    // Block if: (XML/SVG declaration + event handler/script) OR (HTML + script)
    if ((hasXmlDecl && (hasEventHandler || hasScriptTag)) || (hasSvgTag && hasScriptTag)) {
      return {
        valid: false,
        reason: 'contains embedded markup with scripts',
      };
    }

    if (hasHtmlTag || hasScriptTag) {
      return {
        valid: false,
        reason: 'contains embedded HTML or scripts',
      };
    }

    for (let i = 16; i < bytes.length - 4; i++) {
      // Check for ZIP signatures with minimal validation
      if (bytes[i] === 0x50 && bytes[i + 1] === 0x4b) {
        const sig = (bytes[i + 2] << 8) | bytes[i + 3];
        // Only flag if it's a local file header at reasonable offset
        if (sig === 0x0304 && i < 100) {
          return {
            valid: false,
            reason: 'contains embedded ZIP archive',
          };
        }
      }
      // Check for PE executable with validation
      if (bytes[i] === 0x4d && bytes[i + 1] === 0x5a && i + 0x3c + 4 < bytes.length) {
        // Verify it's a valid PE header (has PE offset at MZ+0x3C)
        const peOffsetBytes = bytes.slice(i + 0x3c, i + 0x3c + 4);
        const peOffset =
          peOffsetBytes[0] |
          (peOffsetBytes[1] << 8) |
          (peOffsetBytes[2] << 16) |
          (peOffsetBytes[3] << 24);

        // PE offset should be reasonable (< 1KB) and point to "PE" signature
        if (
          peOffset > 0 &&
          peOffset < 1024 &&
          i + peOffset + 2 < bytes.length &&
          bytes[i + peOffset] === 0x50 &&
          bytes[i + peOffset + 1] === 0x45
        ) {
          return {
            valid: false,
            reason: 'contains embedded executable',
          };
        }
      }
    }
  }

  return { valid: true };
}
